<script setup>
import {computed, onMounted, ref, watch} from 'vue';
import {Inertia} from '@inertiajs/inertia';
import {useForm, usePage} from '@inertiajs/inertia-vue3';
import ConfirmsPassword from '@/Components/ConfirmsPassword.vue';
import DangerButton from '@/Components/DangerButton.vue';
import InputError from '@/Components/InputError.vue';
import InputLabel from '@/Components/InputLabel.vue';
import PrimaryButton from '@/Components/PrimaryButton.vue';
import SecondaryButton from '@/Components/SecondaryButton.vue';
import TextInput from '@/Components/TextInput.vue';

const props = defineProps({
  requiresConfirmation: Boolean,
  provider: Object
});

const enabling = ref(false);
const confirming = ref(false);
const disabling = ref(false);
const qrCode = ref(null);
const setupKey = ref(null);
const recoveryCodes = ref([]);

onMounted(() => {
  if (props.requiresConfirmation === true) {
    showSetupFields()
    enabling.value = false;
    confirming.value = props.requiresConfirmation;
  }
});

const confirmationForm = useForm({
  code: '',
  provider: props.provider.identifier
});

const twoFactorEnabled = computed(
  () => !enabling.value && usePage().props.value.auth.user?.mfa[props.provider.identifier]?.has_secret,
);

watch(twoFactorEnabled, () => {
  if (!twoFactorEnabled.value) {
    confirmationForm.reset();
    confirmationForm.clearErrors();
  }
});

const enableTwoFactorAuthentication = () => {
  enabling.value = true;

  Inertia.post('/user/mfa/enable', {provider: props.provider.identifier}, {
    preserveScroll: true,
    onFinish: () => {
      showSetupFields()
      enabling.value = false;
      confirming.value = props.requiresConfirmation;
    },
  });
};

const showSetupFields = () => {
  return axios.post('/user/mfa/setup', {provider: props.provider.identifier}).then(response => {
    qrCode.value = response.data.challenge;
    setupKey.value = response.data.setup_key;
    recoveryCodes.value = response.data.recovery_codes;
  });
}

const showRecoveryCodes = () => {
  return axios.post('/user/mfa/recovery-codes/show', {provider: props.provider.identifier}).then(response => {
    recoveryCodes.value = response.data.recovery_codes;
  });
};

const confirmTwoFactorAuthentication = () => {
  confirmationForm.post('/user/mfa/confirm', {
    errorBag: "confirmTwoFactorAuthentication",
    preserveScroll: true,
    preserveState: true,
    onSuccess: () => {
      confirming.value = false;
      qrCode.value = null;
      setupKey.value = null;
      recoveryCodes.value = [];
    },
  });
};

const regenerateRecoveryCodes = () => {
  axios
    .post('/user/mfa/recovery-codes', {provider: props.provider.identifier})
    .then(() => showRecoveryCodes());
};

const disableTwoFactorAuthentication = () => {
  disabling.value = true;

  Inertia.post('/user/mfa/disable', {provider: props.provider.identifier}, {
    preserveScroll: true,
    onSuccess: () => {
      disabling.value = false;
      confirming.value = false;
    },
  });
};
</script>

<style scoped>

</style>
<template>
  <div>
    <h3 v-if="twoFactorEnabled && ! confirming" class="text-lg font-medium text-gray-900">
      You have enabled two factor authentication.
    </h3>

    <h3 v-else-if="twoFactorEnabled && confirming" class="text-lg font-medium text-gray-900">
      Finish enabling two factor authentication.
    </h3>

    <h3 v-else class="text-lg font-medium text-gray-900">
      You have not enabled <span v-text="provider.name"></span> two factor authentication.
    </h3>

    <div class="mt-3 max-w-xl text-sm text-gray-600">
      <p>
        When two factor authentication is enabled, you will be prompted for a secure, random token during
        authentication. You may retrieve this token from your phone's Google Authenticator application.
      </p>
    </div>

    <div v-if="twoFactorEnabled">
      <div v-if="qrCode">
        <div class="mt-4 max-w-xl text-sm text-gray-600">
          <p v-if="provider.identifier === 'sms'"></p>
          <p v-else-if="confirming" class="font-semibold">
            To finish enabling two factor authentication, scan the following QR code using your phone's
            authenticator
            application or enter the setup key and provide the generated OTP code.
          </p>

          <p v-else>
            Two factor authentication is now enabled. Scan the following QR code using your phone's authenticator
            application or enter the setup key.
          </p>
        </div>

        <div class="mt-4" v-html="qrCode"/>

        <div v-if="setupKey" class="mt-4 max-w-xl text-sm text-gray-600">
          <p class="font-semibold">
            Setup Key: <span v-html="setupKey"></span>
          </p>
        </div>

        <div v-if="confirming" class="mt-4">
          <InputLabel for="code" value="Code"/>

          <TextInput
            id="code"
            v-model="confirmationForm.code"
            type="text"
            name="code"
            class="block mt-1 w-1/2"
            inputmode="numeric"
            autofocus
            autocomplete="one-time-code"
            @keyup.enter="confirmTwoFactorAuthentication"
          />

          <InputError :message="confirmationForm.errors.code" class="mt-2"/>
        </div>
      </div>

      <div v-if="recoveryCodes.length > 0">
        <div class="mt-4 max-w-xl text-sm text-gray-600">
          <p class="font-semibold">
            Store these recovery codes in a secure password manager. They can be used to recover access to your
            account if your two factor authentication device is lost.
          </p>
        </div>

        <div class="grid gap-1 max-w-xl mt-4 px-4 py-4 font-mono text-sm bg-gray-100 rounded-lg">
          <div v-for="code in recoveryCodes" :key="code">
            {{ code }}
          </div>
        </div>
      </div>
    </div>

    <div class="mt-5 mb-5">
      <div v-if="! twoFactorEnabled">
        <ConfirmsPassword @confirmed="enableTwoFactorAuthentication">
          <PrimaryButton type="button" :class="{ 'opacity-25': enabling }" :disabled="enabling">
            Enable
          </PrimaryButton>
        </ConfirmsPassword>
      </div>

      <div v-else>
        <ConfirmsPassword @confirmed="confirmTwoFactorAuthentication">
          <PrimaryButton
            v-if="confirming"
            type="button"
            class="mr-3"
            :class="{ 'opacity-25': enabling }"
            :disabled="enabling"
          >
            Confirm
          </PrimaryButton>
        </ConfirmsPassword>

        <ConfirmsPassword @confirmed="regenerateRecoveryCodes">
          <SecondaryButton
            v-if="recoveryCodes.length > 0 && ! confirming"
            class="mr-3"
          >
            Regenerate Recovery Codes
          </SecondaryButton>
        </ConfirmsPassword>

        <ConfirmsPassword @confirmed="showRecoveryCodes">
          <SecondaryButton
            v-if="recoveryCodes.length === 0 && ! confirming"
            class="mr-3"
          >
            Show Recovery Codes
          </SecondaryButton>
        </ConfirmsPassword>

        <ConfirmsPassword @confirmed="disableTwoFactorAuthentication">
          <SecondaryButton
            v-if="confirming"
            :class="{ 'opacity-25': disabling }"
            :disabled="disabling"
          >
            Cancel
          </SecondaryButton>
        </ConfirmsPassword>

        <ConfirmsPassword @confirmed="disableTwoFactorAuthentication">
          <DangerButton
            v-if="! confirming"
            :class="{ 'opacity-25': disabling }"
            :disabled="disabling"
          >
            Disable
          </DangerButton>
        </ConfirmsPassword>
      </div>
    </div>
  </div>
</template>
